/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FunnelData } from '@interfaces';
import { BulkMessageModalComponent } from '../dialogs/bulk-message-modal/bulk-message-modal.component';
import { SUBSTAGES_FUNNEL } from '@constants';

@Component({
  selector: 'unicon-dashboard-funnel',
  templateUrl: './dashboard-funnel.component.html',
  styleUrls: ['./dashboard-funnel.component.scss'],
})
export class DashboardFunnelComponent implements OnChanges {
  @Input() funnelData?: FunnelData[][];
  @Input() activeBulkMessage?: boolean;
  @Input() leadsWithoutTemplate: string[] = [];
  @Input() leadsWithTemplate: string[] = [];
  protected tooltipText: string = '';
  protected isloading = false;
  protected showText = true;
  protected readonly funnelContainerHeight = 420;
  protected readonly MIN_STAGE_HEIGHT = 30;

  constructor(
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
  ) {}

  protected getFirstColumnTotal(): number {
    if (!this.funnelData || this.funnelData.length === 0) return 0;
    const first = this.funnelData[0] || [];
    return first.reduce((acc: number, s: any) => acc + Number(s.count ?? 0), 0);
  }

  /**
   * Suma total de counts de una columna (array de stages)
   */
  protected getColumnTotal(column: any[] | undefined): number {
    if (!Array.isArray(column)) return 0;
    return column.reduce(
      (acc: number, s: any) => acc + Number(s.count ?? 0),
      0,
    );
  }

  /**
   * Altura total en pixeles que debe ocupar la columna en relación a la primera columna.
   * Si la primera columna tiene total 0 se utiliza el máximo total entre columnas como referencia.
   */
  protected getColumnHeight(columnIndex: number): number {
    if (!this.funnelData || this.funnelData.length === 0)
      return this.funnelContainerHeight;
    const firstTotal = this.getFirstColumnTotal();
    const column = this.funnelData[columnIndex] || [];
    const colTotal = this.getColumnTotal(column);

    if (firstTotal > 0) {
      return (colTotal / firstTotal) * this.funnelContainerHeight;
    }

    // si firstTotal es 0, usar max total como referencia para distribuir alturas proporcionalmente
    const totals = this.funnelData.map(c => this.getColumnTotal(c));
    const maxTotal = Math.max(...totals, 1);
    return (colTotal / maxTotal) * this.funnelContainerHeight;
  }

  /**
   * Calcula la altura de un stage dentro de su columna (columnIndex) proporcionalmente.
   * Ahora calcula todas las alturas de la columna, aplica un mínimo por stage (30px) cuando corresponde
   * y, si la suma excede la altura de la columna, reduce sólo desde las etapas que tengan altura por
   * encima de su mínimo para que la suma final no supere columnHeight.
   */
  protected getStageHeightForColumn(
    columnIndex: number,
    stage: any,
    sIndex?: number,
  ): number {
    const column = this.funnelData?.[columnIndex] ?? [];
    const colTotal = this.getColumnTotal(column);

    // Si la columna no tiene total, aplicar comportamiento previo mínimo
    if (colTotal <= 0) {
      const min =
        typeof sIndex === 'number' &&
        sIndex === 0 &&
        Number(stage?.count ?? 0) === 0
          ? 30
          : 1;
      return min;
    }

    const columnHeight = this.getColumnHeight(columnIndex);

    // Calcular alturas "raw" proporcionales por cada stage
    const raws = column.map((s: any) => {
      const cnt = Number(s?.count ?? 0);
      return (cnt / colTotal) * columnHeight;
    });

    // Determinar mínimo aplicable por elemento. Reglas:
    // - Si es el primer elemento y su count es 0, mantener mínimo 30 (comportamiento anterior).
    // - Si su count > 0 y su altura raw < MIN_STAGE_HEIGHT, forzar MIN_STAGE_HEIGHT.
    // - En otro caso el mínimo es 0 (puede reducirse para ajustar).
    const mins = column.map((s: any, idx: number) => {
      const cnt = Number(s?.count ?? 0);
      if (idx === 0 && cnt === 0) return 30;
      if (cnt > 0 && raws[idx] < this.MIN_STAGE_HEIGHT)
        return this.MIN_STAGE_HEIGHT;
      return 0;
    });

    // Alturas iniciales aplicando mínimos donde corresponda
    let adjusted = raws.map((r, idx) => Math.max(r, mins[idx]));

    // Asegurar al menos 1px para elementos con count 0 que no tengan mínimos aplicados
    adjusted = adjusted.map((a, idx) =>
      Number(column[idx]?.count ?? 0) === 0 ? Math.max(1, a) : a,
    );

    // Si la suma supera la altura disponible, redistribuir la diferencia restándola sólo de las
    // etapas que tengan altura por encima de su mínimo.
    const total = adjusted.reduce((acc, v) => acc + v, 0);
    if (total > columnHeight) {
      const surplus = total - columnHeight;
      // cuánto puede reducirse de cada etapa (ajustado - su mínimo)
      const reducibles = adjusted.map((a, idx) => Math.max(0, a - mins[idx]));
      const reducibleTotal = reducibles.reduce((acc, v) => acc + v, 0);

      if (reducibleTotal > 0) {
        adjusted = adjusted.map((a, idx) => {
          const available = Math.max(0, a - mins[idx]);
          const reduction = (available / reducibleTotal) * surplus;
          return Math.max(mins[idx], a - reduction);
        });
      } else {
        // Si no hay nada reducible (todos están en su mínimo), escalar todo proporcionalmente
        const scale = columnHeight / total;
        adjusted = adjusted.map((a, idx) => Math.max(mins[idx], a * scale));
      }

      // Corrección por redondeos: ajustar la diferencia restante al elemento con mayor margen
      const newTotal = adjusted.reduce((acc, v) => acc + v, 0);
      const diff = columnHeight - newTotal;
      if (Math.abs(diff) > 0.5) {
        // encontrar índice con mayor (adjusted - min) para darle la diferencia
        let bestIdx = 0;
        let bestVal = adjusted[0] - mins[0];
        for (let i = 1; i < adjusted.length; i++) {
          const val = adjusted[i] - mins[i];
          if (val > bestVal) {
            bestVal = val;
            bestIdx = i;
          }
        }
        adjusted[bestIdx] = Math.max(mins[bestIdx], adjusted[bestIdx] + diff);
      }
    }

    // Determinar índice del stage solicitado. Primero usar sIndex si se pasa, si no, buscar por referencia
    let index = typeof sIndex === 'number' ? sIndex : column.indexOf(stage);
    if (index === -1 && stage?.name) {
      index = column.findIndex((s: any) => s.name === stage.name);
    }

    if (typeof index !== 'number' || index < 0 || index >= adjusted.length) {
      // fallback: devolver el primer elemento o 1px
      return Math.max(1, adjusted[0] ?? 1);
    }

    // devolver altura final garantizando al menos 1px
    return Math.max(1, adjusted[index]);
  }

  /**
   * Compatibilidad: si se llama sin columnIndex, intenta inferir la columna buscando en funnelData.
   */
  protected getStageHeight(
    stage: any,
    fallbackColumnIndex?: number,
    sIndex?: number,
  ): number {
    // si funnelData es array de arrays
    if (Array.isArray(this.funnelData) && this.funnelData.length > 0) {
      // si se provee índice, usarlo
      if (typeof fallbackColumnIndex === 'number') {
        const adjustHeight = this.getStageHeightForColumn(
          fallbackColumnIndex,
          stage,
          sIndex,
        );
        return adjustHeight > 30 ? adjustHeight : 30;
      }

      // intentar encontrar la columna que contiene este stage por referencia (obj equality) o por nombre
      for (let ci = 0; ci < this.funnelData.length; ci++) {
        const col = this.funnelData[ci] || [];
        if (col.includes(stage)) {
          return this.getStageHeightForColumn(ci, stage, sIndex);
        }
        // fallback: buscar por nombre
        if (col.some((s: any) => s.name === stage?.name)) {
          return this.getStageHeightForColumn(ci, stage, sIndex);
        }
      }

      // si no lo encontramos, devolver 0
      return 0;
    }

    return 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['funnelData']) {
      this.funnelData = this.funnelData ?? [];
    }

    if (changes['leadsWithoutTemplate']) {
      this.leadsWithoutTemplate = this.leadsWithoutTemplate ?? [];
    }

    if (changes['leadsWithTemplate']) {
      this.leadsWithTemplate = this.leadsWithTemplate ?? [];
    }

    if (changes['activeBulkMessage']) {
      this.cd.detectChanges();
    }
  }

  protected showTooltip(stage: FunnelData) {
    stage.showTooltip = true;
  }

  protected hideTooltip(stage: FunnelData) {
    if (
      SUBSTAGES_FUNNEL[1] !== stage.name ||
      (!this.activeBulkMessage && SUBSTAGES_FUNNEL[1] !== stage.name) ||
      (!this.activeBulkMessage && SUBSTAGES_FUNNEL[1] === stage.name)
    ) {
      stage.showTooltip = false;
    }
  }

  protected hideTooltipBulk(stage: FunnelData) {
    stage.showTooltip = false;
  }

  protected trackByColumn(index: number, column: any) {
    return index + '-' + (Array.isArray(column) ? column.length : '0');
  }

  protected trackByStage(index: number, stage: any) {
    return stage?.name ?? index;
  }

  protected getMaxCount(column: any[]): number {
    if (!Array.isArray(column) || column.length === 0) return 0;
    return Math.max(...column.map(s => Number(s?.count ?? 0)));
  }

  /**
   * Decide si se debe mostrar el label (name y count) de un stage.
   * Reglas:
   * - siempre mostrar para el primer item (sIndex === 0)
   * - para los demás, sólo mostrar si su altura computada es >= 30px
   */
  protected shouldShowStageLabel(
    columnIndex: number,
    sIndex: number,
    stage: any,
  ): boolean {
    if (typeof sIndex !== 'number') return true;
    if (sIndex === 0) return true;
    const height = this.getStageHeightForColumn(columnIndex, stage, sIndex);
    return Number(height) >= 30;
  }

  protected showModalResponse(name: string, count: number) {
    this.dialog.open(BulkMessageModalComponent, {
      disableClose: false,
      autoFocus: false,
      width: '680px',
      maxWidth: '95vw',
      data: {
        name,
        count,
        leadsWithoutTemplate: this.leadsWithoutTemplate,
        leadsWithTemplate: this.leadsWithTemplate,
      },
    });
  }
}
